<script lang="ts">
import { computed, defineComponent, onActivated, ref, watch } from "vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Zeile from "@/components/grid/Zeile.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Zelle from "@/components/grid/Zelle.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import dateUtils from "@/businesslogic/utils/dateUtils";
import vorversicherungsUtils from "@/components/vorversicherung/vorversicherungsUtils";
import Logger from "js-logger";
import storeUtils from "@/businesslogic/utils/storeUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "Vorversicherung",
  components: {
    TitelZelle,
    Zelle,
    Ueberschrift,
    Zeile,
    ValidierungsFehler,
  },
  props: {
    fehler: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const piniaStore = useFormStore();

    const { dateStringToIsoDate, getIsoDate, getIsoDatePlusYears, splitDateString, getLocalDate, getNow } = dateUtils();

    const {
      korrektur2902,
      berechneVersicherungsBeginnMin,
      versicherungsBeginnMinErsterTagDesJahres,
      bereinigeVorversicherung,
    } = vorversicherungsUtils();

    const { istVhvVerkaufsprodukt } = storeUtils();

    const beginnInEinemJahr = computed(() => {
      const beginn = dateStringToIsoDate(piniaStore.state.tarifinformationen.versicherungsbeginn);

      if (beginn) {
        beginn.setFullYear(beginn.getFullYear() + 1);
        return getLocalDate(beginn);
      }

      return undefined;
    });

    function sollGekuendigtVonVHVSetzen() {
      return (
        piniaStore.state.vorversicherung.antragsart === "wechsel" &&
        piniaStore.state.isMakler === true &&
        istVhvVerkaufsprodukt(piniaStore.state)
      );
    }

    const versicherungsBeginn = ref(piniaStore.state.tarifinformationen.versicherungsbeginn as string);

    const maxVersicherungsBeginn = computed(() => {
      return getIsoDatePlusYears(2);
    });

    const versicherungsBeginnMin = computed(() => {
      return berechneVersicherungsBeginnMin(piniaStore.state);
    });

    const naechsterJanuar = computed(() => {
      const { jahr } = splitDateString(piniaStore.state.tarifinformationen.versicherungsbeginn);
      if (isNaN(jahr)) {
        return "derzeit nicht berechenbar";
      }

      return "01.01." + (jahr + 2);
    });

    const bhOderBl = (verkaufsprodukt: string): boolean => {
      return verkaufsprodukt.toLowerCase() === "bl" || verkaufsprodukt.toLowerCase() === "bh";
    };

    const antragsartSichtbar = computed(() => {
      return bhOderBl(piniaStore.state.verkaufsprodukt);
    });

    watch(
      () => piniaStore.state.verkaufsprodukt,
      (verkaufsprodukt) => {
        if (bhOderBl(verkaufsprodukt)) {
          piniaStore.state.vorversicherung.antragsart = "neu";
        }
      },
    );

    watch(versicherungsBeginn, (versicherungsBeginn) => {
      const beginnDate = new Date(versicherungsBeginn);

      if (beginnDate >= getNow() && beginnDate <= new Date(maxVersicherungsBeginn.value)) {
        piniaStore.state.tarifinformationen.versicherungsbeginn = versicherungsBeginn;
      }
    });

    const ablaufSichtbar = computed(() => {
      return (
        piniaStore.state.isMakler &&
        (piniaStore.state.verkaufsprodukt.toLowerCase() === "phv" ||
          piniaStore.state.verkaufsprodukt.toLowerCase() === "vgv" ||
          istVhvVerkaufsprodukt(piniaStore.state) ||
          piniaStore.state.verkaufsprodukt.toLowerCase() === "hg")
      );
    });

    const beginnErsterTagDesJahres = computed(() => {
      return versicherungsBeginnMinErsterTagDesJahres(piniaStore.state);
    });

    watch(
      () => piniaStore.state.vorversicherung.vertragEnde,
      () => {
        const beginnMin = dateStringToIsoDate(berechneVersicherungsBeginnMin(piniaStore.state));

        Logger.log("beginnMin", beginnMin);

        if (beginnMin) {
          versicherungsBeginn.value = getIsoDate(korrektur2902(beginnMin));
        }
      },
    );

    watch(
      () => piniaStore.state.vorversicherung.antragsart,
      () => {
        bereinigeVorversicherung(piniaStore.state.vorversicherung, sollGekuendigtVonVHVSetzen());
      },
    );
    const istAenderungsantragMitVorbelegung = computed(() => {
      return piniaStore.state.vorbelegungsId !== "" && "NEUORDNUNG" === piniaStore.state?.subStatus;
    });

    onActivated(() => {
      if (bhOderBl(piniaStore.state.verkaufsprodukt) && piniaStore.state.vorversicherung.antragsart === "") {
        piniaStore.state.vorversicherung.antragsart = "neu";
      }
    });

    return {
      piniaStore,
      props,
      versicherungsBeginnMin,
      maxVersicherungsBeginn,
      antragsartSichtbar,
      istAenderungsantragMitVorbelegung,
      naechsterJanuar,
      ablaufSichtbar,
      beginnInEinemJahr,
      beginnErsterTagDesJahres,
      versicherungsBeginn,
    };
  },
});
</script>

<template>
  <ueberschrift titel="Angaben zur Versicherung" />
  <!-- Antragsart -->
  <template v-if="!antragsartSichtbar">
    <zeile>
      <titel-zelle>Ihr Anliegen</titel-zelle>
      <zelle :breite="8" class="mkRadio">
        <!-- Antagsart ins Modell aufnehmen -->
        <input
          type="radio"
          id="vorversicherungAntragsartFirstTime"
          name="vorversicherungAntragsart"
          v-model="piniaStore.state.vorversicherung.antragsart"
          value="neu"
          required
          aria-required="true"
          aria-label="Erstmalig versichern"
          :disabled="istAenderungsantragMitVorbelegung"
          data-cy="vorversicherungAntragsartErstmalig"
        />
        <label
          for="vorversicherungAntragsartFirstTime"
          class="radiolabel"
          :class="{
            'radiolabel--disabled': istAenderungsantragMitVorbelegung,
          }"
          >Erstmalig versichern</label
        >
        <input
          type="radio"
          id="vorversicherungAntragsartTypeTransfer"
          name="vorversicherungAntragsart"
          v-model="piniaStore.state.vorversicherung.antragsart"
          value="wechsel"
          aria-label="Versicherer wechseln"
          :disabled="istAenderungsantragMitVorbelegung"
          data-cy="vorversicherungAntragsartWechsel"
        />
        <label
          for="vorversicherungAntragsartTypeTransfer"
          class="radiolabel"
          :class="{
            'radiolabel--disabled': istAenderungsantragMitVorbelegung,
          }"
          >Versicherer wechseln</label
        >
        <input
          type="radio"
          id="vorversicherungAntragsartModification"
          name="vorversicherungAntragsart"
          v-model="piniaStore.state.vorversicherung.antragsart"
          value="aenderung"
          aria-label="Bestehenden Vertrag ändern"
          data-cy="vorversicherungAntragsartAenderung"
        />
        <label for="vorversicherungAntragsartModification" class="radiolabel">Bestehenden Vertrag ändern</label>
        <validierungs-fehler :fehler-text="fehler.vorversicherungAntragsart" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.vorversicherung.antragsart === 'neu' && piniaStore.state.verkaufsprodukt === 'VGV'">
      <titel-zelle>Bestand für das Gebäude eine Versicherung?</titel-zelle>
      <zelle :breite="8" class="mkRadio">
        <input
          type="radio"
          id="vorversicherungVorhandenNein"
          name="vorversicherungVorhanden"
          v-model="piniaStore.state.vorversicherung.vorhanden"
          :value="false"
          required
          aria-required="true"
          aria-label="Nein, es bestand keine Vorversicherung"
          :disabled="istAenderungsantragMitVorbelegung"
          data-cy="vorversicherungVorhandenNein"
        />
        <label
          for="vorversicherungVorhandenNein"
          class="radiolabel"
          :class="{
            'radiolabel--disabled': istAenderungsantragMitVorbelegung,
          }"
          >Nein</label
        >
        <input
          type="radio"
          id="vorversicherungVorhandenJa"
          name="vorversicherungVorhanden"
          v-model="piniaStore.state.vorversicherung.vorhanden"
          :value="true"
          aria-label="Ja, es bestand eine Vorversicherung"
          :disabled="istAenderungsantragMitVorbelegung"
          data-cy="vorversicherungVorhandenJa"
        />
        <label
          for="vorversicherungVorhandenJa"
          class="radiolabel"
          :class="{
            'radiolabel--disabled': istAenderungsantragMitVorbelegung,
          }"
          >Ja</label
        >
        <validierungs-fehler :fehler-text="fehler.vorversicherungVorhanden" :reduzierter-abstand="true" />
      </zelle>
    </zeile>

    <div
      v-if="
        piniaStore.state.vorversicherung.antragsart === 'wechsel' || piniaStore.state.vorversicherung.vorhanden === true
      "
    >
      <!-- Versicherwechsel: Bisheriger Versicherer -->
      <zeile>
        <titel-zelle v-if="piniaStore.state.vorversicherung.antragsart === 'wechsel'"
          >Bisheriger Versicherer</titel-zelle
        >
        <titel-zelle v-else>Vorversicherung des Gebäudes</titel-zelle>
        <zelle>
          <input
            type="text"
            id="vorversicherungGesellschaft"
            name="vorversicherungGesellschaft"
            v-model.lazy.trim="piniaStore.state.vorversicherung.gesellschaft"
            class="c-form-control__input"
            placeholder="Versicherungsgesellschaft"
            minlength="2"
            maxlength="40"
            :required="
              piniaStore.state.vorversicherung.antragsart === 'wechsel' ||
              (piniaStore.state.vorversicherung.antragsart === 'neu' &&
                piniaStore.state.vorversicherung.vorhanden === true)
            "
            :aria-required="
              piniaStore.state.vorversicherung.antragsart === 'wechsel' ||
              (piniaStore.state.vorversicherung.antragsart === 'neu' &&
                piniaStore.state.vorversicherung.vorhanden === true)
            "
            aria-label="Name des Vorversicherers"
            data-fehlermeldung="Bitte geben Sie den Namen des Vorversicherers ein (max. 40 Zeichen)"
            data-cy="vorversicherungGesellschaft"
          />
          <validierungs-fehler :fehler-text="fehler.vorversicherungGesellschaft" />
        </zelle>
      </zeile>
      <zeile v-if="piniaStore.state.isMakler">
        <titel-zelle>Versicherungsscheinnummer</titel-zelle>
        <zelle>
          <input
            type="text"
            id="versicherungsscheinnummer-transfer"
            name="versicherungsscheinnummer"
            v-model.lazy.trim="piniaStore.state.vorversicherung.versicherungsscheinnummer"
            class="c-form-control__input"
            placeholder="Versicherungsscheinnummer"
            minlength="2"
            maxlength="30"
            aria-label="Versicherungsscheinnummer"
            data-fehlermeldung="Bitte geben Sie die Versicherungsscheinnummer ein (max. 30 Zeichen)"
            data-cy="versicherungsscheinnummer-transfer"
          />
          <validierungs-fehler :fehler-text="fehler.versicherungsscheinnummer" />
        </zelle>
      </zeile>
      <!-- Versicherwechsel: Kündigungsstatus -->
      <zeile
        v-if="
          piniaStore.state.vorversicherung.antragsart === 'wechsel' ||
          piniaStore.state.vorversicherung.vorhanden === true
        "
      >
        <titel-zelle>Wurde bereits gekündigt?</titel-zelle>
        <zelle>
          <select
            id="vorversicherungGekuendigtVon"
            name="vorversicherungGekuendigtVon"
            v-model="piniaStore.state.vorversicherung.gekuendigtVon"
            class="c-form-control-select"
            :required="
              piniaStore.state.vorversicherung.antragsart === 'wechsel' ||
              (piniaStore.state.vorversicherung.antragsart === 'neu' &&
                piniaStore.state.vorversicherung.vorhanden === true)
            "
            :aria-required="
              piniaStore.state.vorversicherung.antragsart === 'wechsel' ||
              (piniaStore.state.vorversicherung.antragsart === 'neu' &&
                piniaStore.state.vorversicherung.vorhanden === true)
            "
            aria-label="Wurde bereits gekündigt?"
            data-fehlermeldung="Bitte wählen Sie den Kündigungsstatus aus."
          >
            <option value="">Bitte wählen</option>
            <option value="yesByClient">
              {{
                piniaStore.state.isMakler
                  ? "Ja, die Kündigung ist durch den Versicherungsnehmer erfolgt"
                  : "Ja, ich habe bereits gekündigt"
              }}
            </option>
            <option value="yesByInsurer">
              {{
                piniaStore.state.isMakler
                  ? "Ja, die Versicherung hat dem Versicherungsnehmer gekündigt"
                  : "Ja, meine Versicherung hat mir gekündigt"
              }}
            </option>
            <option value="no">Nein</option>
          </select>
          <validierungs-fehler :fehler-text="fehler.vorversicherungGekuendigtVon" :reduzierter-abstand="true" />
        </zelle>
      </zeile>
      <!-- Versicherwechsel: Vertragsende Vorversicherer -->

      <zeile>
        <titel-zelle v-if="piniaStore.state.vorversicherung.antragsart === 'wechsel'"
          >Vertragsende beim Vorversicherer
        </titel-zelle>
        <titel-zelle v-else>Vertragsende der Vorversicherung</titel-zelle>
        <zelle :breite="3">
          <input
            type="date"
            id="vorversicherungVertragEnde"
            name="vorversicherungVertragEnde"
            v-model.lazy="piniaStore.state.vorversicherung.vertragEnde"
            class="c-form-control__input"
            title="Enddatum im Format TT.MM.JJJJ"
            minlength="10"
            maxlength="10"
            pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
            :required="
              piniaStore.state.vorversicherung.antragsart === 'wechsel' ||
              (piniaStore.state.vorversicherung.antragsart === 'neu' &&
                piniaStore.state.vorversicherung.vorhanden === true)
            "
            :aria-required="
              piniaStore.state.vorversicherung.antragsart === 'wechsel' ||
              (piniaStore.state.vorversicherung.antragsart === 'neu' &&
                piniaStore.state.vorversicherung.vorhanden === true)
            "
            aria-label="Vertragsende"
            data-fehlermeldung="Bitte geben Sie das Ablaufdatum des bisherigen Vertrags ein (TT.MM.JJJJ)"
            data-cy="vorversicherungVertragEnde"
          />
          <validierungs-fehler :fehler-text="fehler.vorversicherungVertragEnde" />
        </zelle>
      </zeile>
    </div>

    <div v-if="piniaStore.state.vorversicherung.antragsart === 'aenderung'">
      <zeile v-if="piniaStore.state.isMakler">
        <titel-zelle>Versicherungsscheinnummer</titel-zelle>
        <zelle>
          <input
            type="text"
            id="versicherungsscheinnummer-modification"
            name="versicherungsscheinnummer"
            v-model.lazy.trim="piniaStore.state.vorversicherung.versicherungsscheinnummer"
            class="c-form-control__input"
            placeholder="Versicherungsscheinnummer"
            minlength="2"
            maxlength="40"
            :disabled="istAenderungsantragMitVorbelegung"
            aria-label="Versicherungsscheinnummer"
            data-fehlermeldung="Bitte geben Sie die Versicherungsscheinnummer ein (max. 40 Zeichen)"
            data-cy="versicherungsscheinnummer-modification"
          />
          <validierungs-fehler :fehler-text="fehler.versicherungsscheinnummer" />
        </zelle>
      </zeile>
    </div>
  </template>

  <!-- Versicherungsbeginn -->
  <zeile>
    <titel-zelle>Versicherungsbeginn bei der GEV (12:00 Uhr)</titel-zelle>
    <zelle :breite="3">
      <input
        type="date"
        id="tariffStartDate"
        name="tariffStartDate"
        v-model.lazy.trim="versicherungsBeginn"
        class="c-form-control__input"
        data-cy="versicherungsbeginn"
        title="Versicherungsbeginn im Format TT.MM.JJJJ"
        :min="versicherungsBeginnMin"
        :max="maxVersicherungsBeginn"
        minlength="10"
        maxlength="10"
        pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
        required
        aria-required="true"
        aria-label="Versicherungsbeginn bei der GEV"
      />
      <validierungs-fehler :fehler-text="fehler.tariffStartDate" />
    </zelle>
    <zelle :breite="4" v-if="piniaStore.state.vorversicherung.antragsart === 'wechsel'"
      ><span style="color: #666666">
        Für lückenlosen Versicherungsschutz empfehlen wir,
        <strong>Vertragsende</strong> und <strong>Versicherungsbeginn am gleichen Tag</strong> zu wählen.
        <strong>Ausnahme: 29. Februar und rückwirkender Versicherungsbeginn</strong></span
      >
    </zelle>
  </zeile>

  <zeile v-if="ablaufSichtbar">
    <titel-zelle>Ablauf</titel-zelle>
    <zelle class="mkRadio">
      <input
        v-model="piniaStore.state.tarifinformationen.ablauf"
        type="radio"
        id="ablaufEinJahr"
        name="ablauf"
        value="einJahr"
        data-cy="ablaufEinJahr"
        required
        aria-required="true"
        aria-label="Versicherungsablauf"
      />
      <label for="ablaufEinJahr" class="radiolabel">{{ beginnInEinemJahr }}</label>
      <input
        v-model="piniaStore.state.tarifinformationen.ablauf"
        type="radio"
        id="ablaufJanuar"
        name="ablauf"
        value="januar"
        data-cy="ablaufJanuar"
        aria-label="Versicherungsablauf zum 01. Januar"
      />
      <label v-if="!beginnErsterTagDesJahres" for="ablaufJanuar" class="radiolabel">
        {{ naechsterJanuar }}
      </label>
      <validierungs-fehler :fehler-text="fehler.ablauf" :reduzierter-abstand="true" />
    </zelle>
  </zeile>
</template>
